import { Segment, Table as SemanticTable, TableBody, TableHeader, TableRow } from "semantic-ui-react";

import React from "react";

import renderChildren from "../../../utils/renderChildren";
import LoadingContext from "../../ui/components/LoadingContext/LoadingContext";

import useTable from "../hooks/useTable";

import TableCell from "./TableCell";
import TableCellCheckbox from "./TableCellCheckbox";
import TableCellDropdownMenu from "./TableCellDropdownMenu";
import TableCellValue from "./TableCellValue";
import TableColumnSelector from "./TableColumnSelector";
import TableContext from "./TableContext";
import TableHeaderCell from "./TableHeaderCell";
import TableHeaderCellCheckbox from "./TableHeaderCellCheckbox";

export default function Table({
	data,
	columns,
	onClick,
	children,
	loading = false,
	error = false,
	allowEmpty,
	visibleColumns,
	defaultVisibleColumns,
	enableExpander,
	enableCheckboxes,
	enableColumnSelector,
	getDropdownMenuOptions,
	noDataAvailableMessage,
	onChangeVisibleColumns,
	getRowDisabled = (_) => false,
	style,
	...props
}) {
	const table = useTable({ data, columns, visibleColumns, defaultVisibleColumns });

	const { filteredColumns } = table;

	return (
		<TableContext.Provider value={table}>
			<LoadingContext
				data={data}
				error={error}
				loading={loading}
				allowEmpty={allowEmpty}
				noDataAvailableMessage={noDataAvailableMessage}
			>
				<Segment
					attached={enableExpander && "top"}
					style={{ padding: 0, overflowX: "auto", scrollbarWidth: "thin", ...style }}
					{...props}
				>
					<SemanticTable padded unstackable selectable={!!onClick} style={{ border: "none" }}>
						<TableHeader>
							<TableRow>
								{enableCheckboxes && <TableHeaderCellCheckbox enableCheckboxes={enableCheckboxes} />}

								{filteredColumns.map((column) => (
									<TableHeaderCell key={column.id} {...column}>
										{renderChildren(column.Header, { column })}
									</TableHeaderCell>
								))}

								{enableColumnSelector && (
									<TableColumnSelector onChangeVisibleColumns={onChangeVisibleColumns} />
								)}

								{!enableColumnSelector && getDropdownMenuOptions && <TableHeaderCell />}
							</TableRow>
						</TableHeader>

						<TableBody>
							{data.map((row, index) => {
								const isDisabled = getRowDisabled(row);

								return (
									<TableRow key={row.id}>
										{enableCheckboxes && (
											<TableCellCheckbox row={row} enableCheckboxes={enableCheckboxes} />
										)}

										{filteredColumns.map((column) => (
											<TableCell
												key={column.id}
												onClick={!isDisabled && onClick ? () => onClick(row) : undefined}
												disabled={isDisabled}
												{...column}
											>
												<TableCellValue row={row} index={index} column={column} data={data} />
											</TableCell>
										))}

										{getDropdownMenuOptions && (
											<TableCellDropdownMenu options={getDropdownMenuOptions(row, index)} />
										)}

										{!getDropdownMenuOptions && enableColumnSelector && <TableHeaderCell />}
									</TableRow>
								);
							})}
						</TableBody>
					</SemanticTable>
				</Segment>

				{enableExpander && (
					<Segment transparent attached="bottom" textAlign="center" style={{ background: "none" }}></Segment>
				)}
			</LoadingContext>

			{renderChildren(children, table)}
		</TableContext.Provider>
	);
}
